import { Era, WeekInfo, WeekNote } from '../types';

export const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const MS_PER_WEEK = 7 * MS_PER_DAY;

/**
 * Parses a YYYY-MM-DD string into a local Date object (at midnight)
 */
export function parseISODate(dateStr: string): Date {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return new Date();
  return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
}

/**
 * Formats a Date to YYYY-MM-DD
 */
export function formatISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a Date to human friendly string (e.g. "Oct 12, 2024")
 */
export function formatDisplayDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculates current week index relative to birthdate
 */
export function getCurrentWeekIndex(birthdate: Date): number {
  const now = new Date();
  const diffTime = now.getTime() - birthdate.getTime();
  if (diffTime < 0) return -1;
  return Math.floor(diffTime / MS_PER_WEEK);
}

/**
 * Computes week info for any weekIndex
 */
export function computeWeekInfo(
  weekIndex: number,
  birthDate: Date,
  currentWeekIndex: number,
  eras: Era[],
  notesMap: Map<number, WeekNote>
): WeekInfo {
  const weekStartMs = birthDate.getTime() + weekIndex * MS_PER_WEEK;
  const startDate = new Date(weekStartMs);
  const endDate = new Date(weekStartMs + 6 * MS_PER_DAY);

  const year = Math.floor(weekIndex / 52);
  const weekOfYear = (weekIndex % 52) + 1;

  const isPassed = weekIndex < currentWeekIndex;
  const isCurrent = weekIndex === currentWeekIndex;
  const isFuture = weekIndex > currentWeekIndex;

  const ageYears = year;
  const ageWeeks = weekOfYear;

  // Find all active eras during this week's time range
  const activeEras = eras.filter((era) => {
    const eraStart = parseISODate(era.start_date).getTime();
    const eraEnd = era.end_date ? parseISODate(era.end_date).getTime() + MS_PER_DAY : Infinity;

    // Check if the week intersects with era range
    return weekStartMs + MS_PER_DAY >= eraStart && weekStartMs <= eraEnd;
  });

  const note = notesMap.get(weekIndex);

  return {
    index: weekIndex,
    year,
    weekOfYear,
    startDate,
    endDate,
    isPassed,
    isCurrent,
    isFuture,
    ageYears,
    ageWeeks,
    eras: activeEras,
    note,
  };
}
